import { Injectable } from '@angular/core';
import { combineLatest, map, shareReplay, tap } from 'rxjs';

// --- Services ---
import { LocalStorageService } from '@services/local-storage.service';
import {
	ParametersService,
	type IUpdateParametersParams,
} from '@services/parameters.service';

// --- Stores ---
import { ParametersStore } from '@stores/parameters.store';

// --- Interfaces ---
import type { IParameters } from '@interfaces/parameters';

@Injectable({
	providedIn: 'root',
})
export class ParametersFacade {
	parameters!: IParameters;

	readonly parametersState$ = combineLatest([
		this.parametersStore.parametersState$,
		this.getParameters(),
	]).pipe(
		map(([state]) => state),
		shareReplay({ refCount: true })
	);

	constructor(
		private localStorageService: LocalStorageService,
		private parametersService: ParametersService,
		private parametersStore: ParametersStore
	) {
		this.parameters = this.localStorageService.get('parameters') as IParameters;
	}

	getParameters() {
		return this.parametersService.getParameters().pipe(
			tap(parameters => {
				this.setParameters(parameters);
			})
		);
	}

	setParameters(parameters: IParameters) {
		this.parameters = parameters;
		this.localStorageService.set('parameters', parameters);
		this.parametersStore.updateParameters(parameters);
	}

	getOptions() {
		return this.parametersService.getOptions().pipe(shareReplay());
	}

	getPriceList() {
		return this.parametersService.getPriceList().pipe(shareReplay());
	}

	updateParameters(
		parameters: IParameters,
		queryParams?: IUpdateParametersParams
	) {
		return this.parametersService
			.updateParameters(parameters, queryParams)
			.pipe(
				tap(parameters => {
					this.setParameters(parameters);
				})
			);
	}
}
